import { Container, Graphics, Text, type FederatedPointerEvent } from "pixi.js";
import { tween, easeOutQuad, type Ticker } from "../animation/tween";
import type { AnimationTarget } from "../animation/animations";

export interface RadialAction {
  id: string;
  label: string;
}

const ITEM_SIZE = 44;
const ITEM_GAP = 6;
const MENU_OFFSET_X = 20;
const ANIM_DURATION = 150;
const STAGGER_DELAY = 40;
const BG_COLOR = 0xfffdf5;
const BORDER_COLOR = 0xb9d8aa;
const TEXT_COLOR = 0x173420;

/** A staggered pop-in tween can still be scheduled (via setTimeout) or in flight when `hide()`
 *  destroys the item container — every accessor no-ops once destroyed, since PixiJS nulls out
 *  `scale`/`position` on destroy() and would otherwise throw and freeze the whole ticker loop. */
class MenuAnimationTarget implements AnimationTarget {
  constructor(private readonly container: Container) {}
  get x(): number { return this.container.destroyed ? 0 : this.container.position.x; }
  set x(v: number) { if (!this.container.destroyed) this.container.position.x = v; }
  get y(): number { return this.container.destroyed ? 0 : this.container.position.y; }
  set y(v: number) { if (!this.container.destroyed) this.container.position.y = v; }
  get scaleX(): number { return this.container.destroyed ? 0 : this.container.scale.x; }
  set scaleX(v: number) { if (!this.container.destroyed) this.container.scale.x = v; }
  get scaleY(): number { return this.container.destroyed ? 0 : this.container.scale.y; }
  set scaleY(v: number) { if (!this.container.destroyed) this.container.scale.y = v; }
  get rotation(): number { return this.container.destroyed ? 0 : this.container.rotation; }
  set rotation(v: number) { if (!this.container.destroyed) this.container.rotation = v; }
  get alpha(): number { return this.container.destroyed ? 0 : this.container.alpha; }
  set alpha(v: number) { if (!this.container.destroyed) this.container.alpha = v; }
}

export class RadialMenu {
  readonly container = new Container();
  private items: Container[] = [];
  private targets: MenuAnimationTarget[] = [];
  private onPick?: (id: string) => void;
  private dismissHandler: ((e: FederatedPointerEvent) => void) | undefined;
  private stageRef: Container | undefined;

  constructor(private readonly ticker: Ticker) {
    this.container.eventMode = "passive";
    this.container.zIndex = 10_000;
    this.container.visible = false;
  }

  get isOpen(): boolean {
    return this.container.visible;
  }

  show(
    worldPos: { x: number; y: number },
    entityName: string,
    actions: readonly RadialAction[],
    onPick: (id: string) => void,
    stage: Container,
  ): void {
    this.hide();
    this.onPick = onPick;
    this.stageRef = stage;

    const count = actions.length;

    const nameLabel = new Text({
      text: entityName,
      style: {
        fill: 0xffffff,
        fontSize: 14,
        fontFamily: "system-ui, sans-serif",
        fontWeight: "700",
        dropShadow: {
          color: 0x000000,
          distance: 1,
          alpha: 0.6,
        },
      },
    });
    nameLabel.anchor.set(0, 0.5);
    nameLabel.position.set(MENU_OFFSET_X, 0);
    this.container.addChild(nameLabel);

    for (let i = 0; i < count; i++) {
      const action = actions[i]!;
      const x = MENU_OFFSET_X + ITEM_SIZE / 2;
      const y = (i + 1) * (ITEM_SIZE + ITEM_GAP);

      const item = this.createItem(action, x, y, i);
      this.items.push(item);
      this.targets.push(new MenuAnimationTarget(item));
      this.container.addChild(item);
    }

    this.container.position.set(worldPos.x, worldPos.y);
    this.container.visible = true;

    this.dismissHandler = () => this.hide();
    stage.eventMode = "static";
    const handler = this.dismissHandler;
    setTimeout(() => {
      stage.on("pointerdown", handler);
    }, 0);
  }

  hide(): void {
    for (const item of this.items) {
      item.destroy();
    }
    this.items = [];
    this.targets = [];
    this.container.removeChildren();
    this.container.visible = false;

    if (this.dismissHandler && this.stageRef) {
      this.stageRef.off("pointerdown", this.dismissHandler);
    }
    this.dismissHandler = undefined;
    this.stageRef = undefined;
  }

  private createItem(action: RadialAction, x: number, y: number, index: number): Container {
    const item = new Container();
    item.eventMode = "static";
    item.cursor = "pointer";

    const w = 120;
    const h = ITEM_SIZE;
    const bg = new Graphics();
    bg.roundRect(-w / 2, -h / 2, w, h, 8);
    bg.fill(BG_COLOR);
    bg.stroke({ color: BORDER_COLOR, width: 2 });
    item.addChild(bg);

    const label = new Text({
      text: action.label,
      style: {
        fill: TEXT_COLOR,
        fontSize: 13,
        fontFamily: "system-ui, sans-serif",
        fontWeight: "500",
      },
    });
    label.anchor.set(0.5);
    item.addChild(label);

    item.position.set(x, y);
    item.scale.set(0);

    item.on("pointerdown", (e: FederatedPointerEvent) => {
      e.stopPropagation();
      this.onPick?.(action.id);
      this.hide();
    });

    const target = new MenuAnimationTarget(item);
    const delay = index * STAGGER_DELAY;
    setTimeout(() => {
      void tween(this.ticker, target, { scaleX: 1, scaleY: 1 }, ANIM_DURATION, easeOutQuad);
    }, delay);

    return item;
  }
}
